// src/blogs/headModelUtils.js
// Non-component pieces of the shared head model, split out of HeadModel.jsx
// so that file exports only the Head component itself (react-refresh wants a
// component file to export components only).
import { useMemo } from "react";
import * as THREE from "three";
import { useGLTF } from "@react-three/drei";

const BASE = import.meta.env.BASE_URL;

export const HEAD_URL = `${BASE}blog/head.glb`;
export const HEAD_SIZE = 1.8;

// head.glb is authored facing +X, so it needs a quarter turn to meet the
// camera. Adjust if you swap in a model that faces elsewhere.
export const HEAD_YAW_OFFSET = -Math.PI / 2;

export const STEP = Math.PI / 2; // one click/step = a quarter turn
export const SPIN_DAMP = 4.2;

// An arrow turns the head the opposite way to where it points, so the
// left-hand arrow spins it to its right and the up arrow tips the crown
// toward you. These flip the rendered direction only.
export const YAW_SIGN = 1;
export const PITCH_SIGN = 1;

/**
 * Clones the loaded scene, repaints it, and works out the transform that
 * centres it on the origin at `targetSize`. Cloning matters: callers that
 * need independent materials (e.g. several arrows sharing one cached gltf)
 * must pass their own material instance.
 */
export function useFittedModel(url, targetSize, material) {
  const { scene } = useGLTF(url);

  return useMemo(() => {
    const object = scene.clone(true);
    object.traverse((child) => {
      if (child.isMesh) child.material = material;
    });

    const box = new THREE.Box3().setFromObject(object);
    const size = box.getSize(new THREE.Vector3());
    const scale = targetSize / Math.max(size.x, size.y, size.z);
    const offset = box.getCenter(new THREE.Vector3()).multiplyScalar(-scale);

    return { object, scale, offset };
  }, [scene, targetSize, material]);
}
